import { AxiosInstance } from 'axios';
import {
  ICreatePodRequest,
  IPodMemberResponse,
  IPodPreferencesRequest,
  IPodResponse,
} from '../interfaces/ICommunityHub';
import { IPagedFilterResponse } from '../interfaces/IPagedFilterResponse';
import { buildPodSlug } from '../utils/podSlug';
import type { IReactionSummary } from '../interfaces/ICommunityHub';

function normalisePodMember(source: unknown): IPodMemberResponse {
  const member = (typeof source === 'object' && source !== null ? source : {}) as Record<string, unknown>;

  // Backend PodMemberResponse serialises with explicit snake_case JsonPropertyName
  // overrides (user_id, display_name, avatar_url, joined_at, invited_by), which win
  // over the API's global camelCase naming policy - so both casings are checked here.
  const rawUserId = member.userId ?? member.user_id;
  const rawDisplayName = member.displayName ?? member.display_name;
  const rawAvatarUrl = member.avatarUrl ?? member.avatar_url;
  const rawJoinedAt = member.joinedAt ?? member.joined_at;
  const rawInvitedBy = member.invitedBy ?? member.invited_by;

  return {
    userId: typeof rawUserId === 'string' ? rawUserId : '',
    displayName: typeof rawDisplayName === 'string' ? rawDisplayName : '',
    avatarUrl: typeof rawAvatarUrl === 'string' ? rawAvatarUrl : undefined,
    joinedAt: typeof rawJoinedAt === 'string' ? rawJoinedAt : undefined,
    invitedBy: typeof rawInvitedBy === 'string' ? rawInvitedBy : undefined,
  };
}

function normalisePodResponse(source: unknown): IPodResponse {
  const pod = (typeof source === 'object' && source !== null ? source : {}) as Record<string, unknown>;
  const rawId = pod.id ?? pod.Id;
  const rawName = pod.name ?? pod.Name;
  const rawGradient = pod.gradient ?? pod.Gradient;
  const rawPhotoUrl = pod.imageUrl ?? pod.photoUrl ?? pod.PhotoUrl;
  const rawMemberCount = pod.memberCount ?? pod.MemberCount;
  const rawCreatedAt = pod.createdAt ?? pod.CreatedAt;

  const id = typeof rawId === 'string' ? rawId : '';
  const name = typeof rawName === 'string' ? rawName : 'Untitled Pod';

  // Backend PodResponse returns the current user's preferences as flat
  // `showInHome`/`muted` fields (from their PodMember row) rather than a
  // nested `preferences` object, so build the preferences shape from those.
  const rawShowInHome = pod.showInHome ?? pod.ShowInHome;
  const rawMuted = pod.muted ?? pod.Muted;
  const showInHomeFeed = typeof rawShowInHome === 'boolean' ? rawShowInHome : true;
  const muteNotifications = typeof rawMuted === 'boolean' ? rawMuted : false;

  const rawUnreadChatCount = pod.unreadChatCount ?? pod.UnreadChatCount;
  const rawLastMessagePreview = pod.lastMessagePreview ?? pod.LastMessagePreview;
  const rawLastMessageAt = pod.lastMessageAt ?? pod.LastMessageAt;
  const rawUnreadMessageCount = pod.unreadMessageCount ?? pod.UnreadMessageCount;

  return {
    id,
    communityId: typeof pod.communityId === 'string' ? pod.communityId : '',
    name,
    // Human-readable slug used for the visible browser URL instead of the raw id.
    slug: buildPodSlug(name, id),
    description: typeof pod.description === 'string' ? pod.description : undefined,
    imageUrl: typeof rawPhotoUrl === 'string' ? rawPhotoUrl : undefined,
    gradient: typeof rawGradient === 'string' ? rawGradient : undefined,
    visibility: (typeof pod.visibility === 'string' ? pod.visibility : 'Private') as IPodResponse['visibility'],
    memberCount: typeof rawMemberCount === 'number' ? rawMemberCount : 0,
    show_in_home: showInHomeFeed,
    createdAt: typeof rawCreatedAt === 'string' ? rawCreatedAt : new Date().toISOString(),
    muted: muteNotifications,
    unreadChatCount: typeof rawUnreadChatCount === 'number' ? rawUnreadChatCount : 0,
    lastMessagePreview: typeof rawLastMessagePreview === 'string' ? rawLastMessagePreview : null,
    lastMessageAt: typeof rawLastMessageAt === 'string' ? rawLastMessageAt : null,
    unreadMessageCount: typeof rawUnreadMessageCount === 'number' ? rawUnreadMessageCount : 0,
    preferences: {
      podId: id,
      userId: '',
      show_in_home_feed: showInHomeFeed,
      mute_notifications: muteNotifications,
      updatedAt: new Date().toISOString(),
    },
  };
}


/**
 * Service for Pods API interactions
 * Calls /pods endpoints
 */
export class PodService {
  private readonly baseUrl = '/pods';
  private readonly apiClient: AxiosInstance;

  constructor(apiClient: AxiosInstance) {
    this.apiClient = apiClient;
  }

  /**
   * Get all pods for the current user
   */
  async getPods(): Promise<IPodResponse[]> {
    const response = await this.apiClient.get<unknown[]>(`${this.baseUrl}`);
    return response.data.map(normalisePodResponse);
  }

  /**
   * Get a single pod by ID
   */
  async getPodById(podId: string): Promise<IPodResponse> {
    const response = await this.apiClient.get<unknown>(`${this.baseUrl}/${podId}`);
    return normalisePodResponse(response.data);
  }

  async createPod(request: ICreatePodRequest): Promise<IPodResponse> {
    const response = await this.apiClient.post<unknown>(`${this.baseUrl}`, {
      name: request.name,
      invitedUserIds: request.invitedUserIds,
      inviteeUserIds: request.invitedUserIds,
      memberIds: request.invitedUserIds,
    });

    const payload = typeof response.data === 'object' && response.data !== null && 'pod' in response.data
      ? (response.data as { pod: unknown }).pod
      : response.data;

    return normalisePodResponse(payload);
  }

  /**
   * Update pod preferences (show_in_home_feed, mute_notifications)
   */
  async updatePodPreferences(
    podId: string,
    preferences: IPodPreferencesRequest,
  ): Promise<void> {
    await this.apiClient.patch(
      `${this.baseUrl}/${podId}/preferences`,
      {
        showInHome: preferences.show_in_home_feed,
        muted: preferences.mute_notifications,
      },
    );
  }

  async getPodMembers(podId: string, pageSize = 100): Promise<IPodMemberResponse[]> {
    const response = await this.apiClient.get<IPagedFilterResponse<unknown>>(
      `${this.baseUrl}/${podId}/members`,
      {
        params: {
          page: 1,
          pageSize,
        },
      },
    );

    return response.data.items.map(normalisePodMember);
  }

  async inviteMember(podId: string, userId: string): Promise<void> {
    await this.apiClient.post(`${this.baseUrl}/${podId}/invites`, {
      userId,
    });
  }

  async leavePod(podId: string): Promise<void> {
    await this.apiClient.delete(`${this.baseUrl}/${podId}/members/me`);
  }

  async getMessageReactions(podId: string, messageId: string): Promise<IReactionSummary> {
    return (await this.apiClient.get<IReactionSummary>(`${this.baseUrl}/${podId}/messages/${messageId}/reactions`)).data;
  }

  async setMessageReaction(podId: string, messageId: string, reactionType: string): Promise<IReactionSummary> {
    return (await this.apiClient.post<IReactionSummary>(`${this.baseUrl}/${podId}/messages/${messageId}/reactions`, { reactionType })).data;
  }

  async removeMessageReaction(podId: string, messageId: string): Promise<IReactionSummary> {
    return (await this.apiClient.delete<IReactionSummary>(`${this.baseUrl}/${podId}/messages/${messageId}/reactions`)).data;
  }

  async sendMessage(podId: string, body: string): Promise<unknown> {
    const response = await this.apiClient.post<unknown>(
      `${this.baseUrl}/${podId}/messages`,
      { body },
    );
    return response.data;
  }

  /**
   * Mark a pod as read for the current user. Persists read state so unread
   * counts remain accurate across refreshes/relogin (mirrors conversations).
   */
  async markPodAsRead(podId: string, lastReadMessageId?: string): Promise<void> {
    await this.apiClient.patch(
      `${this.baseUrl}/${podId}/mark-read`,
      undefined,
      lastReadMessageId ? { params: { lastReadMessageId } } : undefined,
    );
  }

  /**
   * Rename a pod.
   */
  async updatePodName(podId: string, name: string): Promise<IPodResponse> {
    const response = await this.apiClient.patch<unknown>(
      `${this.baseUrl}/${podId}`,
      { name },
    );
    return normalisePodResponse(response.data);
  }

  /**
   * Upload/replace a pod's photo. Persists to blob storage server-side so it
   * survives page reloads (unlike a local object-URL preview).
   */
  async uploadPodPhoto(podId: string, file: File): Promise<IPodResponse> {
    const formData = new FormData();
    formData.append('photo', file);
    const response = await this.apiClient.post<unknown>(
      `${this.baseUrl}/${podId}/photo`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return normalisePodResponse(response.data);
  }
}
